import type { ReactNode } from 'react';
import { groupBy, ofKind, type Catalog, type Kind } from '../catalog.ts';

export interface RequirementsValue {
  coversCount: string;
  teamMode: '' | 'ALONE' | 'TEAM';
  requirements: Record<string, { importance: 'REQUIRED' | 'PREFERRED'; minLevel: string }>;
}

export const EMPTY_REQUIREMENTS: RequirementsValue = { coversCount: '', teamMode: '', requirements: {} };

export function requirementsPayload(v: RequirementsValue) {
  return {
    coversCount: v.coversCount === '' ? undefined : Number(v.coversCount),
    teamMode: v.teamMode || undefined,
    requirements: Object.entries(v.requirements).map(([itemId, r]) => ({ itemId, importance: r.importance, minLevel: r.minLevel || undefined })),
  };
}

function Importance({ value, onChange }: { value: 'REQUIRED' | 'PREFERRED'; onChange: (v: 'REQUIRED' | 'PREFERRED') => void }) {
  return (
    <select className="input" style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }} aria-label="Importance" value={value} onChange={(e) => onChange(e.target.value as 'REQUIRED' | 'PREFERRED')}>
      <option value="REQUIRED">Obligatoire</option>
      <option value="PREFERRED">Souhaité</option>
    </select>
  );
}

function Title({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 6 }}>{children}</div>;
}

/**
 * Exigences du poste côté restaurateur. « Obligatoire » écarte les extras qui ne le remplissent pas ;
 * « Souhaité » fait seulement monter ceux qui le remplissent dans le classement.
 */
export function RequirementsEditor({ catalog, value, onChange }: { catalog: Catalog; value: RequirementsValue; onChange: (v: RequirementsValue) => void }) {
  const set = (itemId: string, on: boolean, defaults: { minLevel?: string } = {}) => {
    const next = { ...value.requirements };
    if (on) next[itemId] = { importance: 'REQUIRED', minLevel: '', ...defaults };
    else delete next[itemId];
    onChange({ ...value, requirements: next });
  };
  const patch = (itemId: string, change: Partial<RequirementsValue['requirements'][string]>) => onChange({ ...value, requirements: { ...value.requirements, [itemId]: { ...value.requirements[itemId], ...change } } });

  const roles = ofKind(catalog, 'ROLE');
  const selectedRole = roles.find((r) => value.requirements[r.id]);

  function setRole(id: string) {
    const next = { ...value.requirements };
    for (const r of roles) delete next[r.id]; // un seul poste par mission
    if (id) next[id] = { importance: 'REQUIRED', minLevel: '' };
    onChange({ ...value, requirements: next });
  }

  const checklist = (kind: Kind) =>
    groupBy(ofKind(catalog, kind), (i) => i.group).map(([group, items]) => (
      <div key={group} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {group && <Title>{group}</Title>}
        {items.map((i) => (
          <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <label style={{ display: 'flex', gap: 8, fontSize: 13.5, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={Boolean(value.requirements[i.id])} onChange={(e) => set(i.id, e.target.checked)} style={{ marginTop: 3 }} />
              {i.label}
            </label>
            {value.requirements[i.id] && <Importance value={value.requirements[i.id].importance} onChange={(v) => patch(i.id, { importance: v })} />}
          </div>
        ))}
      </div>
    ));

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontWeight: 800 }}>Profil recherché (facultatif)</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 2 }}>
          Plus vous précisez, mieux les candidatures sont classées. « Obligatoire » écarte les extras qui ne le remplissent pas ; « Souhaité » fait seulement monter ceux qui le remplissent.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Title>Poste</Title>
        <select className="input" aria-label="Poste" value={selectedRole?.id ?? ''} onChange={(e) => setRole(e.target.value)}>
          <option value="">Non précisé</option>
          {groupBy(roles, (r) => r.group).map(([group, list]) => (
            <optgroup key={group} label={group}>{list.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</optgroup>
          ))}
        </select>
        {selectedRole && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select className="input" style={{ flex: 1, minWidth: 150 }} aria-label="Séniorité minimale" value={value.requirements[selectedRole.id].minLevel} onChange={(e) => patch(selectedRole.id, { minLevel: e.target.value })}>
              <option value="">Séniorité indifférente</option>
              {Object.entries(catalog.seniority).map(([k, v]) => <option key={k} value={k}>{v} minimum</option>)}
            </select>
            <Importance value={value.requirements[selectedRole.id].importance} onChange={(v) => patch(selectedRole.id, { importance: v })} />
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <Title>Couverts par service</Title>
          <input className="input" type="number" min={1} max={5000} placeholder="ex. 80" value={value.coversCount} onChange={(e) => onChange({ ...value, coversCount: e.target.value })} aria-label="Nombre de couverts" />
        </div>
        <div>
          <Title>Cadre du poste</Title>
          <select className="input" value={value.teamMode} onChange={(e) => onChange({ ...value, teamMode: e.target.value as RequirementsValue['teamMode'] })} aria-label="Cadre du poste">
            <option value="">Non précisé</option>
            <option value="TEAM">En équipe</option>
            <option value="ALONE">Seul(e) sur le poste</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Title>Logiciel de caisse utilisé</Title>
        {checklist('POS')}
        <Title>Logiciel de réservation</Title>
        {checklist('RESERVATION_TOOL')}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Title>Tenue exigée</Title>
        {checklist('EQUIPMENT')}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Title>Langues</Title>
        {ofKind(catalog, 'LANGUAGE').map((l) => (
          <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', gap: 8, fontSize: 13.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={Boolean(value.requirements[l.id])} onChange={(e) => set(l.id, e.target.checked)} style={{ marginTop: 3 }} />
              {l.label}
            </label>
            {value.requirements[l.id] && (
              <div style={{ display: 'flex', gap: 6 }}>
                <select className="input" style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }} aria-label={`Niveau minimum — ${l.label}`} value={value.requirements[l.id].minLevel} onChange={(e) => patch(l.id, { minLevel: e.target.value })}>
                  <option value="">Tout niveau</option>
                  {Object.entries(catalog.languageLevels).map(([k, v]) => <option key={k} value={k}>{v} minimum</option>)}
                </select>
                <Importance value={value.requirements[l.id].importance} onChange={(v) => patch(l.id, { importance: v })} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Title>Certifications</Title>
        {checklist('CERTIFICATION')}
      </div>
    </div>
  );
}
